// AGC: useInventory Hook
// AGC: Manages inventory data fetching, caching, and mutations
// AGC: Uses TanStack Query for in-memory cache + localStorage for disk persistence
// AGC: Pattern: stale-while-revalidate — cached data displays instantly, fresh data replaces it in background

import { useState, useCallback, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuth } from './useAuth'
import type { InventoryItem, InventoryMovement, InputCategory } from '../types/inventory'
import { getStockStatus } from '../types/inventory'

// =============================================================================
// AGC: TYPES
// =============================================================================

export interface InventorySummary {
  totalItems: number
  totalValue: number
  lowStockCount: number
  outOfStockCount: number
  categoryCounts: Record<InputCategory, number>
}

export interface UseInventoryReturn {
  items: InventoryItem[]
  movements: InventoryMovement[]
  summary: InventorySummary | null
  loading: boolean
  error: string | null
  // AGC: Actions
  refresh: () => Promise<void>
  addItem: (item: Partial<Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<InventoryItem>
  updateItem: (id: string, updates: Partial<InventoryItem>) => Promise<InventoryItem>
  deleteItem: (id: string) => Promise<void>
  ensureItemExists: (inputId: string, inputName: string, category: InputCategory, unit: string, minimumStock?: number) => Promise<InventoryItem>
  recordMovement: (itemId: string, movement: {
    type: 'entry' | 'exit'
    quantity: number
    reason?: string
    source?: string
    relatedTaskId?: string
    relatedLotId?: string
  }) => Promise<InventoryMovement>
  getItemById: (id: string) => InventoryItem | undefined
  getItemsByCategory: (category: InputCategory) => InventoryItem[]
  getLowStockItems: () => InventoryItem[]
  getMovementsForItem: (itemId: string) => Promise<InventoryMovement[]>
}

// =============================================================================
// AGC: LOCALSTORAGE DISK CACHE
// AGC: Gives instant display on mount; TanStack Query refetches in background when stale
// =============================================================================

const INVENTORY_CACHE_KEY = 'droidfarm_inventory_items'

function readDiskCache(): InventoryItem[] | undefined {
  try {
    const raw = localStorage.getItem(INVENTORY_CACHE_KEY)
    if (!raw) return undefined
    return JSON.parse(raw) as InventoryItem[]
  } catch {
    return undefined
  }
}

function writeDiskCache(items: InventoryItem[]): void {
  try {
    localStorage.setItem(INVENTORY_CACHE_KEY, JSON.stringify(items))
  } catch { /* AGC: ignore quota errors */ }
}

// =============================================================================
// AGC: API RESPONSE TYPES
// =============================================================================

interface InventoryListResponse {
  success: boolean
  data: { items: InventoryItem[] } | InventoryItem[]
}

interface InventoryItemResponse {
  success: boolean
  data: InventoryItem
}

interface InventoryMovementResponse {
  success: boolean
  data: {
    movement: InventoryMovement
    updatedItem?: InventoryItem
  }
}

interface MovementHistoryResponse {
  success: boolean
  data: InventoryMovement[] | { movements: InventoryMovement[]; count: number }
}

// =============================================================================
// AGC: MAIN HOOK
// =============================================================================

export const useInventory = (): UseInventoryReturn => {
  const { user } = useAuth()
  const enabled = !!user?.uid
  const qc = useQueryClient()

  // AGC: Session-only movements state (populated by recordMovement, not persisted)
  const [movements, setMovements] = useState<InventoryMovement[]>([])

  // =========================================================================
  // AGC: MAIN QUERY — items list with localStorage stale-while-revalidate
  // =========================================================================
  const {
    data: items = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['inventory-items'],
    queryFn: async (): Promise<InventoryItem[]> => {
      const res = await api.get<InventoryListResponse>('/api/v1/inventory')
      if (!res.success) throw new Error('Failed to fetch inventory')
      // AGC: Backend returns { data: { items: [] } } or { data: [] } (backwards compat)
      const resData = res.data as { items?: InventoryItem[] }
      const arr = Array.isArray(resData?.items)
        ? resData.items
        : (Array.isArray(res.data) ? res.data as InventoryItem[] : [])
      // AGC: Persist to localStorage — next mount renders instantly from disk
      writeDiskCache(arr)
      return arr
    },
    enabled,
    staleTime: 5 * 60 * 1000, // AGC: 5 minutes — background refetch only when stale
    placeholderData: readDiskCache, // AGC: Show cached data while fetching fresh
  })

  const loading = isLoading
  const error = queryError ? (queryError as Error).message : null

  // =========================================================================
  // AGC: SUMMARY (derived from items)
  // =========================================================================
  const summary = useMemo<InventorySummary | null>(() => {
    if (!Array.isArray(items) || items.length === 0) return null

    const categoryCounts: Record<InputCategory, number> = {
      herbicide: 0, fungicide: 0, insecticide: 0, acaricide: 0,
      nematicide: 0, fertilizer: 0, soil_amendment: 0, seed: 0,
      inoculant: 0, adjuvant: 0, fuel: 0, machinery_parts: 0,
      packaging: 0, tool: 0, equipment: 0, household: 0, food: 0, other: 0,
    }

    let totalValue = 0
    let lowStockCount = 0
    let outOfStockCount = 0

    items.forEach(item => {
      if (categoryCounts[item.category as InputCategory] !== undefined) {
        categoryCounts[item.category as InputCategory]++
      }
      totalValue += (item.quantity || 0) * (item.costPerUnit || 0)
      const status = getStockStatus(item.quantity, item.minimumStock || 0, item.maximumStock)
      if (status === 'low' || status === 'critical') lowStockCount++
      if (status === 'out') outOfStockCount++
    })

    return { totalItems: items.length, totalValue, lowStockCount, outOfStockCount, categoryCounts }
  }, [items])

  // =========================================================================
  // AGC: MUTATIONS — optimistic updates keep UI and disk cache in sync
  // =========================================================================

  // AGC: Helper to update both query cache and localStorage at once
  const updateCacheAndDisk = useCallback((updater: (old: InventoryItem[]) => InventoryItem[]) => {
    qc.setQueryData<InventoryItem[]>(['inventory-items'], (old) => {
      const updated = updater(old || [])
      writeDiskCache(updated)
      return updated
    })
  }, [qc])

  // AGC: Add item
  const addItemMutation = useMutation({
    mutationFn: (item: Partial<Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>>) =>
      api.post<InventoryItemResponse>('/api/v1/inventory', item),
    onSuccess: (res) => {
      if (res.success && res.data) {
        updateCacheAndDisk((old) => [...old, res.data])
      }
    },
  })

  const addItem = useCallback(async (
    item: Partial<Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<InventoryItem> => {
    const res = await addItemMutation.mutateAsync(item)
    if (!res.success) throw new Error('Failed to add item')
    return res.data
  }, [addItemMutation])

  // AGC: Update item
  const updateItemMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<InventoryItem> }) =>
      api.put<InventoryItemResponse>(`/api/v1/inventory/${id}`, updates),
    onSuccess: (res, { id }) => {
      if (res.success && res.data) {
        updateCacheAndDisk((old) => old.map(item => item.id === id ? { ...item, ...res.data } : item))
      }
    },
  })

  const updateItem = useCallback(async (
    id: string, updates: Partial<InventoryItem>
  ): Promise<InventoryItem> => {
    const res = await updateItemMutation.mutateAsync({ id, updates })
    if (!res.success) throw new Error('Failed to update item')
    return res.data
  }, [updateItemMutation])

  // AGC: Delete item
  const deleteItemMutation = useMutation({
    mutationFn: (id: string) => api.delete<{ success: boolean }>(`/api/v1/inventory/${id}`),
    onSuccess: (res, id) => {
      if (res.success) {
        updateCacheAndDisk((old) => old.filter(item => item.id !== id))
      }
    },
  })

  const deleteItem = useCallback(async (id: string): Promise<void> => {
    const res = await deleteItemMutation.mutateAsync(id)
    if (!res.success) throw new Error('Failed to delete item')
  }, [deleteItemMutation])

  // AGC: Record movement (entry/exit)
  const recordMovement = useCallback(async (
    itemId: string,
    movement: {
      type: 'entry' | 'exit'
      quantity: number
      reason?: string
      source?: string
      relatedTaskId?: string
      relatedLotId?: string
      warehouseId?: string
      locationType?: 'floor' | 'silo' | 'tank'
      containerId?: string
      sourceChannel?: 'ui' | 'sota' | 'whatsapp' | 'system' | 'api'
      rawCommandText?: string
      matchResultId?: string
      correctionApplied?: boolean
    }
  ): Promise<InventoryMovement> => {
    const res = await api.post<InventoryMovementResponse>('/api/v1/inventory/movement', {
      inventoryItemId: itemId,
      ...movement,
      warehouseId: movement.warehouseId || 'warehouse01',
      locationType: movement.locationType || 'floor',
    })

    if (!res.success) throw new Error('Failed to record movement')

    const newMovement = res.data.movement
    const updatedItem = res.data.updatedItem

    // AGC: Update local movements list
    setMovements(prev => [newMovement, ...prev])

    // AGC: Update item quantity in cache + disk
    if (updatedItem) {
      updateCacheAndDisk((old) =>
        old.map(item => item.id === itemId ? { ...item, quantity: updatedItem.quantity } : item)
      )
    }

    return newMovement
  }, [updateCacheAndDisk])

  // =========================================================================
  // AGC: REFRESH — invalidate query to trigger background refetch
  // =========================================================================
  const refresh = useCallback(async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['inventory-items'] })
  }, [qc])

  // AGC: Listen for 'refresh-inventory' events dispatched by voice entry and MainMenu
  useEffect(() => {
    const onRefresh = () => { void refresh() }
    window.addEventListener('refresh-inventory', onRefresh)
    return () => window.removeEventListener('refresh-inventory', onRefresh)
  }, [refresh])

  // =========================================================================
  // AGC: ENSURE ITEM EXISTS — check local cache first, create if missing
  // =========================================================================
  const ensureItemExists = useCallback(async (
    inputId: string,
    inputName: string,
    category: InputCategory,
    unit: string,
    minimumStock: number = 0
  ): Promise<InventoryItem> => {
    const existing = items.find(item =>
      item.inputId === inputId ||
      item.name.toLowerCase() === inputName.toLowerCase()
    )
    if (existing) return existing

    return addItem({
      userId: '',
      farmId: '',
      inputId,
      name: inputName,
      category,
      quantity: 0,
      unit,
      minimumStock,
      notes: 'Auto-criado de recomendação de cultivo',
    })
  }, [items, addItem])

  // =========================================================================
  // AGC: DERIVED GETTERS
  // =========================================================================
  const getItemById = useCallback((id: string): InventoryItem | undefined => {
    return items.find(item => item.id === id)
  }, [items])

  const getItemsByCategory = useCallback((category: InputCategory): InventoryItem[] => {
    return items.filter(item => item.category === category)
  }, [items])

  const getLowStockItems = useCallback((): InventoryItem[] => {
    return items.filter(item => {
      const status = getStockStatus(item.quantity, item.minimumStock || 0, item.maximumStock)
      return status === 'low' || status === 'critical' || status === 'out'
    })
  }, [items])

  // AGC: Fetch movements for a specific item (on-demand, not cached)
  const getMovementsForItem = useCallback(async (itemId: string): Promise<InventoryMovement[]> => {
    try {
      const res = await api.get<MovementHistoryResponse>(
        `/api/v1/inventory/movements/history?itemId=${itemId}`
      )
      if (!res.success) return []
      const payload = res.data
      return Array.isArray(payload)
        ? payload
        : (Array.isArray((payload as { movements?: InventoryMovement[] })?.movements)
          ? (payload as { movements: InventoryMovement[] }).movements
          : [])
    } catch (err) {
      console.error('AGC: Error fetching movements for item:', err)
      return []
    }
  }, [])

  return {
    items,
    movements,
    summary,
    loading,
    error,
    refresh,
    addItem,
    updateItem,
    deleteItem,
    ensureItemExists,
    recordMovement,
    getItemById,
    getItemsByCategory,
    getLowStockItems,
    getMovementsForItem,
  }
}

export default useInventory
